import { useState } from 'react';
import { Trash2 } from 'lucide-react';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { useToast } from '../../components/Toast';
import { BotSubNav } from './BotSubNav';
import {
  TEMPLATE_CATEGORIES,
  TEMPLATE_CATEGORY_LABELS_HE,
  type BotMessageTemplateRow,
  type TemplateCategory,
} from './bot.schema';
import {
  useBotMessageTemplates,
  useUpsertTemplate,
  useSoftDeleteTemplate,
} from './botQueries';
import styles from './bot.module.css';

interface EditState {
  id: string | null;
  slug: string;
  category: TemplateCategory | '';
  label_he: string;
  body_he: string;
  is_active: boolean;
  sort_order: number;
}

const BLANK: EditState = {
  id: null,
  slug: '',
  category: '',
  label_he: '',
  body_he: '',
  is_active: true,
  sort_order: 100,
};

export function MessageTemplatesPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const toast = useToast();

  const { data: templates = [], isLoading } = useBotMessageTemplates(orgId);
  const upsert = useUpsertTemplate(orgId);
  const softDelete = useSoftDeleteTemplate(orgId);

  const [edit, setEdit] = useState<EditState | null>(null);

  function startNew() {
    setEdit({ ...BLANK });
  }

  function startEdit(t: BotMessageTemplateRow) {
    setEdit({
      id: t.id,
      slug: t.slug,
      category: (t.category ?? ''),
      label_he: t.label_he,
      body_he: t.body_he,
      is_active: t.is_active,
      sort_order: t.sort_order,
    });
  }

  async function save() {
    if (!edit) return;
    if (!edit.slug.trim() || !edit.label_he.trim() || !edit.body_he.trim()) {
      toast.error('צריך לפחות slug, תווית, תוכן');
      return;
    }
    try {
      await upsert.mutateAsync({
        id: edit.id,
        values: {
          slug: edit.slug.trim(),
          category: edit.category ? edit.category : null,
          label_he: edit.label_he.trim(),
          body_he: edit.body_he,
          is_active: edit.is_active,
          sort_order: edit.sort_order,
        },
      });
      toast.success('התבנית נשמרה');
      setEdit(null);
    } catch {
      toast.error('לא הצלחנו לשמור · נסה שוב');
    }
  }

  async function remove(t: BotMessageTemplateRow) {
    if (!window.confirm(`למחוק את "${t.label_he}"?`)) return;
    try {
      await softDelete.mutateAsync(t.id);
      toast.success('התבנית נמחקה');
    } catch {
      toast.error('לא הצלחנו למחוק · נסה שוב');
    }
  }

  // Group by category
  const byCategory = new Map<TemplateCategory | 'none', BotMessageTemplateRow[]>();
  for (const t of templates) {
    const key = (t.category ?? 'none');
    const arr = byCategory.get(key) ?? [];
    arr.push(t);
    byCategory.set(key, arr);
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>תבניות הודעה</h1>
          <p className={styles.subtitle}>הודעות מוכנות שהבוט שולח. ערוך את הניסוח שמתאים לך.</p>
        </div>
        <button type="button" className={styles.btnPrimary} onClick={startNew}>
          הוסף תבנית
        </button>
      </header>

      <BotSubNav />

      {isLoading ? (
        <p className={styles.notice}>טוען...</p>
      ) : templates.length === 0 && !edit ? (
        <p className={styles.empty}>טרם הוגדרו תבניות הודעה.</p>
      ) : (
        <>
          {TEMPLATE_CATEGORIES.map((cat) => {
            const items = byCategory.get(cat) ?? [];
            if (items.length === 0) return null;
            return (
              <section key={cat} className={styles.section}>
                <h2 className={styles.sectionTitle}>{TEMPLATE_CATEGORY_LABELS_HE[cat]}</h2>
                <div className={styles.list}>
                  {items.map((t) => (
                    <div key={t.id} className={styles.tableRow} style={{ gridTemplateColumns: 'minmax(120px,200px) 1fr 90px 60px 60px' }}>
                      <span><strong>{t.label_he}</strong></span>
                      <span className={styles.rowMeta} style={{ whiteSpace: 'pre-wrap' }}>{t.body_he}</span>
                      <span className={styles.rowMeta}>
                        <code dir="ltr">{t.slug}</code>
                      </span>
                      <button type="button" className={styles.btnSecondary} onClick={() => { startEdit(t); }}>
                        ערוך
                      </button>
                      <button type="button" className={styles.btnDanger} onClick={() => { void remove(t); }}>
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
          {(byCategory.get('none') ?? []).length > 0 ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>ללא קטגוריה</h2>
              <div className={styles.list}>
                {(byCategory.get('none') ?? []).map((t) => (
                  <div key={t.id} className={styles.tableRow} style={{ gridTemplateColumns: 'minmax(120px,200px) 1fr 90px 60px 60px' }}>
                    <span><strong>{t.label_he}</strong></span>
                    <span className={styles.rowMeta} style={{ whiteSpace: 'pre-wrap' }}>{t.body_he}</span>
                    <span className={styles.rowMeta}>
                      <code dir="ltr">{t.slug}</code>
                    </span>
                    <button type="button" className={styles.btnSecondary} onClick={() => { startEdit(t); }}>ערוך</button>
                    <button type="button" className={styles.btnDanger} onClick={() => { void remove(t); }}>
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}

      {edit ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{edit.id ? 'עריכת תבנית' : 'תבנית חדשה'}</h2>
          <div className={styles.form}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="t-slug">slug (מזהה תכנותי, באנגלית)</label>
              <input
                id="t-slug"
                className={styles.input}
                dir="ltr"
                value={edit.slug}
                onChange={(e) => { setEdit({ ...edit, slug: e.target.value }); }}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="t-category">קטגוריה</label>
              <select
                id="t-category"
                className={styles.select}
                value={edit.category}
                onChange={(e) => { setEdit({ ...edit, category: e.target.value as TemplateCategory | '' }); }}
              >
                <option value="">ללא</option>
                {TEMPLATE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{TEMPLATE_CATEGORY_LABELS_HE[c]}</option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="t-label">תווית (לתצוגה)</label>
              <input
                id="t-label"
                className={styles.input}
                value={edit.label_he}
                onChange={(e) => { setEdit({ ...edit, label_he: e.target.value }); }}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="t-body">תוכן ההודעה</label>
              <textarea
                id="t-body"
                className={styles.textarea}
                value={edit.body_he}
                onChange={(e) => { setEdit({ ...edit, body_he: e.target.value }); }}
              />
            </div>
            <div className={styles.profileActions}>
              <button type="button" className={styles.btnSecondary} onClick={() => { setEdit(null); }}>
                בטל
              </button>
              <button type="button" className={styles.btnPrimary} onClick={() => { void save(); }} disabled={upsert.isPending}>
                שמור
              </button>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
